/** Email-draft review endpoints (SA-99 spec §7). snake_case wire shape. */
import { apiClient, ApiError } from './apiClient'

/** SA-1040 PR 2: the quote version an offer email offers. */
export interface EmailDraftQuote {
  order_id: string
  order_number: string
  version_number: number
  amount_cents: number
  currency: string
  status: string
  document_name: string | null
  document_byte_size: number | null
  document_sha256: string | null
}

export interface EmailDraft {
  id: string
  mailbox_id: string
  conversation_id: string
  status: 'pending' | 'sending' | 'sent' | 'discarded' | 'superseded'
  to_addr: string
  subject: string
  body_text: string
  edited_by_user_id: string | null
  sent_at: string | null
  sent_by_user_id: string | null
  last_error: string | null
  quote_id?: string | null
  quote?: EmailDraftQuote | null
  /** SA-1215: who signs an offer email, resolved to a name; absent on any other draft. */
  signed_by_name?: string | null
  signed_by_viewer?: boolean
}

const base = (conversationId: string) =>
  `/api/inbox/conversations/${conversationId}/email-draft`

export const emailDraftsService = {
  /** null = no pending draft (404). Only a 404 is "absence, not fatal" —
   * apiClient.get throws ApiError (with `.status`) on any non-OK response,
   * so any other status (auth failure, 500, ...) must surface to the
   * caller rather than be swallowed as "no draft". */
  get: async (conversationId: string): Promise<EmailDraft | null> => {
    try {
      return await apiClient.get<EmailDraft>(base(conversationId))
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null
      throw err
    }
  },
  /** `quote_document_sha256` (SA-1040 PR 2): the hash of the quotation the panel showed; a save of an
   *  offer email whose quotation moved since is refused `quote_changed` and writes nothing. */
  update: (conversationId: string,
           body: { subject?: string; body_text?: string; quote_document_sha256?: string }) =>
    apiClient.put<EmailDraft>(base(conversationId), body),
  regenerate: (conversationId: string) =>
    apiClient.post<EmailDraft>(`${base(conversationId)}/regenerate`, {}),
  /**
   * `body` optionally names the draft the staff member reviewed
   * (`draft_id` — a mismatch against the currently-pending draft means it
   * was superseded, e.g. by a processor auto-draft, between their read and
   * their click) and/or the exact subject/body they saw, so the send
   * dispatches precisely what was reviewed rather than "whatever is
   * pending now" — closes the send-TOCTOU + onBlur-save race.
   * `quote_document_sha256` (SA-1040 PR 2): the hash of the quotation the panel showed.
   */
  send: (
    conversationId: string,
    body?: { draft_id?: string; subject?: string; body_text?: string; quote_document_sha256?: string },
  ) => apiClient.post<EmailDraft>(`${base(conversationId)}/send`, body ?? {}),
  discard: (conversationId: string) =>
    apiClient.post<EmailDraft>(`${base(conversationId)}/discard`, {}),
}
